import i18next, { SUPPORTED_LANGUAGES, SupportedLanguages } from '../locales/i18n';
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from 'react';

export function useRouteVariants(): { route: string, href: string, lang: SupportedLanguages }[] {
    const { i18n } = useTranslation();
    const location = useLocation();

    const fetchInvariantRoute = useCallback((route: string | null | undefined, lang: string): null | string => {
        if (route === undefined || route === null) {
            return null;
        }

        if (route === '/' || route === '') {
            return '/';
        }

        if (!SUPPORTED_LANGUAGES.includes(lang as SupportedLanguages)) {
            return '/';
        }

        const pathSegments = route.split('/');
        if (pathSegments.length === 2 || pathSegments.length === 3 && pathSegments[2] == '') {
            return '/lang';
        }
        
        const data = i18n.getDataByLanguage(lang);
        if (!data?.routes) {
            return null;
        }

        pathSegments[1] = 'lang';
        const locRoutes = Object.entries(data.routes);
        for (let i = 2; i != pathSegments.length; ++i) {
            let match: [string, string] | undefined;
            if (undefined === (match = locRoutes.find(([_, value]) => value === pathSegments[i]))) {
                continue;
            }
            
            pathSegments[i] = match[0];
        }

        return pathSegments.join('/');
    }, []);

    const fetchLocalizedRoute = useCallback((route: string | null | undefined, lang: string): null | string => {
        if (route === undefined || route === null) {
            return null;
        }

        if (route === '/' || route === '') {
            return '/';
        }

        if (!SUPPORTED_LANGUAGES.includes(lang as SupportedLanguages)) {
            return '/';
        }

        const data = i18next.getDataByLanguage(lang);
        if (!data?.routes) {
            return null;
        }

        const pathSegments = route.split('/');
        if (pathSegments.length === 2 || pathSegments.length === 3 && pathSegments[2] == '') {
            return `/${lang}`;
        }

        pathSegments[1] = lang;
        for (let i = 2; i != pathSegments.length; ++i) {
            if (!i18next.exists(pathSegments[i], { ns: 'routes', lng: lang })) {
                continue;
            }

            pathSegments[i] = i18next.t(pathSegments[i], { ns: 'routes', lng: lang, defaultValue: pathSegments[i] });
        }

        return pathSegments.join('/');
    }, []);

    return useMemo<{ route: string, href: string, lang: SupportedLanguages }[]>(() => {
        const invariantRoute  = fetchInvariantRoute(location.pathname, i18n.language);
        return SUPPORTED_LANGUAGES
            .map(lang => {
                const localizedPathname = fetchLocalizedRoute(invariantRoute, lang) || '/';
                const fullHref = `${window.location.origin}${localizedPathname}`;
                return {
                    lang,
                    href: fullHref,
                    route: localizedPathname,
                };
            });
    }, [location.pathname, i18n.language]);
}